import { ComponentConfig } from '@measured/puck';
import { MarginProps, marginFields } from '../../puck/reusable-props/margin';
import { PaddingProps, paddingFields } from '../../puck/reusable-props/padding';
import { General } from './general';
import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';
import { ReduxSelectorProps } from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import InfoIcon from '@mui/icons-material/Info';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
} from '../../utils/helper';
import { Switch, Tooltip } from '@mui/material';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';

export type GeneralProps = {
  Title: string;
  Description: string;
  DefaultCollapse?: boolean;
};

export type GeneralTermsProps = GeneralProps &
  MarginProps &
  PaddingProps &
  showComponentProps & {
    border: IBorderValueProps;
  };

export const GeneralTerms = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<GeneralTermsProps> => {
  return {
    label: (
      <CustomTooltip title="GeneralTerms">
        <span>General Terms</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      Title: {
        ...QuillTextEditor('Title', true, 1).content,
      },
      Description: {
        ...QuillTextEditor('Description', true, 2).content,
      },
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={value => onChange({ ...value })}
            title="Border"
          />
        ),
      },
      DefaultCollapse: {
        type: 'custom',
        render: ({ onChange }) => {
          return (
            <>
              <div className="flex justify-between items-center mb-3">
                <p className="text-sm font-semibold">Default Expansion</p>
                <div className="flex gap-0.5 items-center">
                  <Tooltip
                    title={
                      'Toggling this will either collapse or expand the General Terms by default.'
                    }
                    placement="top-start">
                    <InfoIcon fontSize="small" className="h-1 w-1" />
                  </Tooltip>
                  <Switch
                    defaultChecked={false}
                    onChange={e => {
                      onChange(e.target.checked);
                    }}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </div>
              </div>
            </>
          );
        },
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      Title:
        '<p style="line-height: 1.5;"><strong style="font-size: 14px;">View Billing Terms</strong></p>',
      Description:
        '<p style="line-height:1.5;"><span style="font-size: 14px;">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna</span></p>',
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      ...borderProps,
      DefaultCollapse: false,
    },
    render: ({
      margin,
      padding,
      Title,
      Description,
      showComponent,
      DefaultCollapse,
      editMode,
      border,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const state = useAppSelector(state => state);
      const newDescription = getQuillEditorText(Description, state);
      const newTitle = getQuillEditorText(Title, state);
      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              style={{
                ...marginStyles,
                ...getPaddingStyles(padding, currentDeviceType),
                ...getBorderProps(border, currentDeviceType),
              }}
              className={`flex flex-col w-full ${border?.borderStyle?.[currentDeviceType]}`}>
              <General
                Title={newTitle}
                Description={newDescription}
                useAppSelector={useAppSelector}
                open={DefaultCollapse || false}
              />
            </div>
          )}
        </>
      );
    },
  };
};
